import React, { useEffect, useState } from 'react';
import {
  Paper, Typography, Box, Button, Table, TableBody, TableCell, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Autocomplete, Chip, CircularProgress
} from '@mui/material';
import axios from '../../api/client';
import { toast } from 'react-toastify';
import { useCurrency } from '../../context/CurrencyContext';
import ScrollTable from '../ScrollTable';

const RentIncreaseManagement = () => {
  const { formatMoney } = useCurrency();
  const [rows, setRows] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [tenantSearch, setTenantSearch] = useState('');
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    tenantId: '',
    newRent: '',
    noticePeriodDays: 30,
    noticeDate: '',
    effectiveDate: '',
    notes: ''
  });

  const load = async () => {
    setLoading(true);
    try {
      const incRes = await axios.get('/rent-increases');
      setRows(incRes.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Could not load rent increases');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // The tenants endpoint caps a page at 100, so the picker searches the server as the user
  // types rather than filtering a single fetched page.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setTenantsLoading(true);
      try {
        const res = await axios.get('/tenants', { params: { limit: 100, search: tenantSearch.trim() } });
        const body = res.data;
        const list = Array.isArray(body) ? body : (body?.data || []);
        if (!cancelled) setTenants(list.filter((t) => t.unitId || t.unit));
      } catch (error) {
        if (!cancelled) toast.error(error.response?.data?.message || 'Could not load tenants');
      } finally {
        if (!cancelled) setTenantsLoading(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [open, tenantSearch]);

  const tenantLabel = (t) => `${t.name}${t.unit?.unitNumber ? ` — ${t.unit.unitNumber}` : ''}`
    + `${t.unit?.monthlyRent != null ? ` (now ${formatMoney(t.unit.monthlyRent)})` : ''}`;

  const create = async () => {
    try {
      await axios.post('/rent-increases', {
        ...form,
        newRent: parseFloat(form.newRent),
        noticePeriodDays: parseInt(form.noticePeriodDays, 10) || 30
      });
      toast.success('Rent increase notice created and sent');
      setOpen(false);
      setSelectedTenant(null);
      setTenantSearch('');
      setForm({ tenantId: '', newRent: '', noticePeriodDays: 30, noticeDate: '', effectiveDate: '', notes: '' });
      load();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not create increase');
    }
  };

  const act = async (id, action) => {
    try {
      await axios.post(`/rent-increases/${id}/${action}`);
      toast.success(action === 'cancel' ? 'Cancelled' : `Marked ${action}ed`);
      load();
    } catch (error) {
      toast.error(error.response?.data?.message || `Could not ${action}`);
    }
  };

  const statusColor = (s) => ({
    pending: 'warning',
    accepted: 'info',
    applied: 'success',
    declined: 'default',
    cancelled: 'default'
  }[s] || 'default');

  return (
    <Paper sx={{ p: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Rent increases</Typography>
        <Button variant="contained" onClick={() => setOpen(true)}>New increase</Button>
      </Box>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Independent of tenant exit. Rent on the unit changes only after the tenant accepts
        and the effective date is reached.
      </Typography>

      {loading ? <CircularProgress /> : (
        <ScrollTable>
          <TableHead>
            <TableRow>
              <TableCell>Tenant</TableCell>
              <TableCell>Unit</TableCell>
              <TableCell align="right">Current</TableCell>
              <TableCell align="right">New</TableCell>
              <TableCell align="right">Increment</TableCell>
              <TableCell>Effective</TableCell>
              <TableCell>Respond by</TableCell>
              <TableCell>Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell>{r.tenant?.name || '—'}</TableCell>
                <TableCell>{r.unit?.unitNumber || '—'}</TableCell>
                <TableCell align="right">{formatMoney(r.currentRent)} {r.currency}</TableCell>
                <TableCell align="right">{formatMoney(r.newRent)} {r.currency}</TableCell>
                <TableCell align="right">{formatMoney(r.incrementAmount)} {r.currency}</TableCell>
                <TableCell>{r.effectiveDate}</TableCell>
                <TableCell>{r.respondByDate}</TableCell>
                <TableCell><Chip size="small" label={r.status} color={statusColor(r.status)} /></TableCell>
                <TableCell>
                  {r.status === 'pending' && (
                    <>
                      <Button size="small" onClick={() => act(r.id, 'accept')}>Accept</Button>
                      <Button size="small" onClick={() => act(r.id, 'decline')}>Decline</Button>
                      <Button size="small" color="inherit" onClick={() => act(r.id, 'cancel')}>Cancel</Button>
                    </>
                  )}
                  {r.status === 'accepted' && (
                    <Button size="small" color="inherit" onClick={() => act(r.id, 'cancel')}>Cancel</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!rows.length && (
              <TableRow><TableCell colSpan={9}>No rent increases yet.</TableCell></TableRow>
            )}
          </TableBody>
        </ScrollTable>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>New rent increase</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Autocomplete
            options={tenants}
            loading={tenantsLoading}
            // Filtering is done by the server; the default client filter would hide matches
            // on phone or email that are not in the label.
            filterOptions={(x) => x}
            value={selectedTenant}
            onChange={(e, t) => {
              setSelectedTenant(t);
              setForm({ ...form, tenantId: t ? (t.id || t._id) : '' });
            }}
            onInputChange={(e, v, reason) => { if (reason === 'input') setTenantSearch(v); }}
            getOptionLabel={tenantLabel}
            isOptionEqualToValue={(a, b) => (a.id || a._id) === (b.id || b._id)}
            renderOption={(props, t) => (
              <li {...props} key={t.id || t._id}>{tenantLabel(t)}</li>
            )}
            renderInput={(params) => <TextField {...params} label="Tenant" placeholder="Type to search" />}
          />
          <TextField
            label="New monthly rent"
            type="number"
            value={form.newRent}
            onChange={(e) => setForm({ ...form, newRent: e.target.value })}
          />
          <TextField
            label="Notice period (days)"
            type="number"
            value={form.noticePeriodDays}
            onChange={(e) => setForm({ ...form, noticePeriodDays: e.target.value })}
          />
          <TextField
            label="Notice date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={form.noticeDate}
            onChange={(e) => setForm({ ...form, noticeDate: e.target.value })}
            helperText="Leave blank for today"
          />
          <TextField
            label="Effective date (new rent begins)"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={form.effectiveDate}
            onChange={(e) => setForm({ ...form, effectiveDate: e.target.value })}
            helperText="Leave blank to use notice date + notice period"
          />
          <TextField
            label="Notes"
            multiline
            minRows={2}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!form.tenantId || !form.newRent}
            onClick={create}
          >
            Create & notify
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default RentIncreaseManagement;
